import '@shui/core/sh-snackbar';

import { Component, input, booleanAttribute, output, ChangeDetectionStrategy } from '@angular/core';

@Component({
    selector: 'sh-snackbar',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.accessible-action-label]': 'accessibleActionLabel() ?? null',
        '[attr.accessible-close-label]': 'accessibleCloseLabel() ?? null',
        '[attr.action-label]': 'actionLabel() ?? null',
        '[attr.dismissible]': 'dismissible() ? "" : null',
        '[attr.icon]': 'icon() ?? null',
        '[attr.icon-color]': 'iconColor() ?? null',
        '[attr.label]': 'label() ?? null',
        '[attr.label-rows]': 'labelRows() ?? null',
        '[attr.position]': 'position() ?? null',
        '[attr.progress-state]': 'progressState() ?? null',
        '[attr.progress-value]': 'progressValue() ?? null',
        '[attr.render-target]': 'renderTarget() ?? null',
        '[attr.timeout]': 'timeout() ?? null',
        '[attr.type]': 'type() ?? null',
        '(action-clicked)': 'actionClicked.emit($event)',
        '(dismissed)': 'dismissed.emit($event)',
        '(shown)': 'shown.emit($event)'
    }
})
export class ShSnackbarComponent {
    /** Accessible label for the action button */
    public readonly accessibleActionLabel = input<string | number | undefined>(undefined);

    /** Accessible label for the close button */
    public readonly accessibleCloseLabel = input<string | number | undefined>(undefined);

    /** Renders one action button with this label; clicking emits action-clicked. */
    public readonly actionLabel = input<string | number | undefined>(undefined);

    /** Shows a close button that emits dismissed when activated. */
    public readonly dismissible = input(false, { transform: booleanAttribute });

    /** Explicit icon to render; overriddden by the type-specific icon if type is set. */
    public readonly icon = input<string | number | undefined>(undefined);

    /** Overrides icon color for explicit icon. */
    public readonly iconColor = input<string | number | undefined>(undefined);

    /** Message text shown on a single line; overflow truncates with ellipsis. */
    public readonly label = input<string | number | undefined>(undefined);

    /** Number of lines for the label text. Can be 1 (default) or 2. */
    public readonly labelRows = input<string | number | undefined>(undefined);

    /** Placement within the render target with consistent edge spacing. */
    public readonly position = input<string | number | undefined>(undefined);

    /** Progress state for type="progress". Can be "indeterminate" or "paused". */
    public readonly progressState = input<string | number | undefined>(undefined);

    /** Progress value (0-100) for determinate progress when type="progress". */
    public readonly progressValue = input<string | number | undefined>(undefined);

    /**
     * Where the snackbar renders; invalid ids warn and fallback to body. Moves without losing
     * state. Parent container must have position relative when using `parent` or custom element
     * targets.
     */
    public readonly renderTarget = input<string | number | undefined>(undefined);

    /** Auto-dismiss delay in ms; hover pauses, mouseleave resumes. */
    public readonly timeout = input<string | number | undefined>(undefined);

    /** Allows to add a type specific icon for the snackbar, will override icon attribute. */
    public readonly type = input<string | number | undefined>(undefined);

    public readonly actionClicked = output<Event>();

    public readonly dismissed = output<Event>();

    public readonly shown = output<Event>();
}
